import { getSessionLanguageCode } from "../utils/CommonUtils.js"
import packageJson from "../../package.json";

/* ------------------------------------------------------------------
 * Device / app info for login history (OsVersion, ManufactureModel, AppVersion)
 *
 * OsVersion        -> detected at runtime from the user's browser
 * ManufactureModel -> detected at runtime (browser + Teams client type)
 * AppVersion       -> build-time: package.json (web) / Teams manifest (Teams)
 *
 * Website: OsVersion "Windows 11", ManufactureModel "Web - Chrome 130",          AppVersion "Web 1.0.0"
 * Teams:   OsVersion "Windows 11", ManufactureModel "Teams desktop - Edge 130",  AppVersion "Teams 1.0.44"
 * ------------------------------------------------------------------ */

// Website version: "version" in package.json.
export const WEB_APP_VERSION = packageJson.version; // e.g. "1.0.0"

// Teams version: "version" in appPackage/manifest.json. Browsers can't read files
// and Create React App blocks imports from outside src/, so the "start" and
// "build" scripts in package.json read the manifest and pass it in here.
export const TEAMS_APP_VERSION = process.env.REACT_APP_TEAMS_APP_VERSION || "unknown"; // e.g. "1.0.44"

const getUserAgent = () =>
    (typeof navigator !== "undefined" && navigator.userAgent) || "";

// Same rule CalendarEntryPoint uses to decide Web vs Teams.
export const isRunningInTeams = () => {
    try {
        return typeof window !== "undefined" && window.self !== window.top;
    } catch {
        return true; // cross-origin access to window.top throws only inside an iframe
    }
};

// Teams client type ("desktop" / "web" / "android" / "ios"), set by
// CalendarEntryPoint once the Teams context is available.
let teamsClientType = null;

export const setTeamsContext = (context) => {
    teamsClientType = context?.app?.host?.clientType || null;
};

// Chromium browsers (Chrome, Edge, Brave, Teams desktop) only put "156.0.0.0"
// in the user-agent string. The full version (e.g. "156.0.7340.55") comes from
// navigator.userAgentData, loaded once in the background (see refineUaData).
// Brave also sends the same user-agent as Chrome, so it is identified here too.
let fullVersionList = null;

const CHROMIUM_BRANDS = {
    Brave: /brave/i,
    Edge: /microsoft edge/i,
    Opera: /opera/i,
    Chrome: /google chrome/i,
};

const findBrandVersion = (name) => {
    const pattern = CHROMIUM_BRANDS[name];
    if (!pattern) return null;
    try {
        const brands =
            fullVersionList ||
            (typeof navigator !== "undefined" && navigator.userAgentData?.brands) ||
            [];
        return brands.find((b) => pattern.test(b.brand))?.version || null;
    } catch {
        return null;
    }
};

// "156.0.0.0" (reduced user-agent) -> "156"; real versions are kept as they are.
const cleanUaVersion = (version) => version.replace(/^(\d+)\.0\.0\.0$/, "$1");

const detectBrowser = (ua) => {
    const braveVersion = findBrandVersion("Brave");
    if (braveVersion) return `Brave ${braveVersion}`;

    const rules = [
        [/Edg(?:e|A|iOS)?\/([\d.]+)/, "Edge"],
        [/OPR\/([\d.]+)/, "Opera"],
        [/SamsungBrowser\/([\d.]+)/, "Samsung Internet"],
        [/CriOS\/([\d.]+)/, "Chrome"],
        [/FxiOS\/([\d.]+)/, "Firefox"],
        [/Firefox\/([\d.]+)/, "Firefox"],
        [/Chrome\/([\d.]+)/, "Chrome"],
        [/Version\/([\d.]+).*Safari/, "Safari"],
    ];
    for (const [regex, name] of rules) {
        const match = ua.match(regex);
        if (match) return `${name} ${findBrandVersion(name) || cleanUaVersion(match[1])}`;
    }
    return "Unknown Browser";
};

const detectOs = (ua) => {
    let match;
    if (/Windows NT 10\.0/.test(ua)) return "Windows 10"; // refined to 11 below where possible
    if ((match = ua.match(/Windows NT ([\d.]+)/))) return `Windows NT ${match[1]}`;
    if ((match = ua.match(/(?:iPhone|iPad|iPod).*?OS (\d+(?:_\d+)*)/))) return `iOS ${match[1].replace(/_/g, ".")}`;
    if ((match = ua.match(/Android (\d+(?:\.\d+)*)/))) return `Android ${match[1]}`;
    if ((match = ua.match(/Mac OS X (\d+(?:[._]\d+)*)/))) return `macOS ${match[1].replace(/_/g, ".")}`;
    if (/CrOS/.test(ua)) return "Chrome OS";
    if (/Linux/.test(ua)) return "Linux";
    return "Unknown OS";
};

// Browsers freeze the OS version in the user-agent string (Windows 11 reports as
// 10, macOS as 10.15.7). Chromium browsers (Chrome, Edge, Brave, Teams desktop)
// expose the real OS version and full browser version via userAgentData, so
// load both once in the background. Ready long before the user logs in.
let refinedOsVersion = null;

const refineUaData = async () => {
    try {
        const uaData = typeof navigator !== "undefined" ? navigator.userAgentData : null;
        if (!uaData?.getHighEntropyValues) return;
        const values = await uaData.getHighEntropyValues(["platformVersion", "fullVersionList"]);
        if (Array.isArray(values.fullVersionList) && values.fullVersionList.length) {
            fullVersionList = values.fullVersionList;
        }
        const { platform, platformVersion } = values;
        if (!platformVersion) return;
        if (platform === "Windows") {
            refinedOsVersion = parseInt(platformVersion, 10) >= 13 ? "Windows 11" : "Windows 10";
        } else if (platform === "macOS") {
            refinedOsVersion = `macOS ${platformVersion.replace(/\.0$/, "")}`;
        }
    } catch {
        // keep the user-agent based values
    }
};

refineUaData();

export const getOsVersion = () => refinedOsVersion || detectOs(getUserAgent());

export const getManufactureModel = () => {
    const browser = detectBrowser(getUserAgent());
    if (!isRunningInTeams()) return `Web - ${browser}`;
    return teamsClientType ? `Teams ${teamsClientType} - ${browser}` : `Teams - ${browser}`;
};

export const getAppVersion = () =>
    isRunningInTeams() ? `Teams ${TEAMS_APP_VERSION}` : `Web ${WEB_APP_VERSION}`;

export const getDeviceInfo = () => ({
    OsVersion: getOsVersion(),
    ManufactureModel: getManufactureModel(),
    AppVersion: getAppVersion(),
});

// TEMP (os-model-appversion-logging): prints the device values actually sent in
// an API payload so they can be verified. Remove after verification.
// Note: console.log is silenced where REACT_APP_DISABLE_CONSOLE=1 (production).
export const logDeviceInfoPayload = (source, payload = {}) => {
    if (!payload || !("OsVersion" in payload || "ManufactureModel" in payload || "AppVersion" in payload)) {
        return;
    }
    console.log(`[DeviceInfo] ${source}`, {
        OsVersion: payload.OsVersion,
        ManufactureModel: payload.ManufactureModel,
        AppVersion: payload.AppVersion,
    });
};

export const AppConstant = {
    DEFAULT_MS_HOME_ACCOUNT_ID: "402c478d-0ddc-4f0e-b2da-c3a0d5a9831f",
    DEFAULT_CLIENT_ID: "8d76d67e5605ccf0f97a2ac5ac8bc3da",
    // Detected values (previously hardcoded "13.2.1" / "13" / "Android SDK built for x86").
    get AppVersion() {
        return getAppVersion();
    },
    DeviceID: "51507c0e-f101-4abf-85b9-5ddfa2c9ebb7",
    get OsVersion() {
        return getOsVersion();
    },
    get ManufactureModel() {
        return getManufactureModel();
    },
    App: "1",
    ClaimFromFlag: 0,
    /* AvailabilityCode values for which the map/calendar is hidden and the
    NoSpaceError image + AvailabilityMessage is shown instead:
    497	Vehicle missing! Please add one before you can book.
    498	Fully booked! Try a different date or check back later.
    499	You're early! Booking for this date hasn't started.
    601	You're all set—already booked in [zone-name]. */
    AVAILABILITY_BLOCKED_CODES: new Set([497, 498, 499, 601, 602, 603, 628]),
    Longitude : "",
    Latitude :"",
    RegisterUserUniqueId: "",
    TERMS_LINKS: {
        en: "https://ronspotflexwork.com/terms/",
        fr: "https://ronspotflexwork.com/fr/conditions/",
        es: "https://ronspotflexwork.com/es/condiciones/",
        de: "https://ronspotflexwork.com/de/bedingungen/",
        nl: "https://ronspotflexwork.com/nl/voorwaarden/",
        it: "https://ronspotflexwork.com/it/termini/",
        cs: "https://ronspotflexwork.com/podminky/",
        pl: "https://ronspotflexwork.com/regulamin/",
        ga: "https://ronspotflexwork.com/tearmai/"

    },
    HELP_CENTRE: "https://ronspotflexwork.com/help-centre/",
    //RequestFrom: "react",
    RequestFrom: process.env.REACT_APP_REQUEST_FROM || "",
    // Fixed origin marker used where the API expects the literal "react"
    // (e.g. removeAccount's RequestFrom), independent of the env-based RequestFrom.
    React_hardcode: "react",
    get Language() {
        return getSessionLanguageCode();
    }
}
